
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import "./styles/goalThemes.css";

import { logger } from "./utils/logger";

// Ensure PWA/service-worker updates don't leave users stuck on an old UI build.
// In Lovable preview links (preview--*.lovable.app) we auto-apply updates and reload.
import { registerSW } from "virtual:pwa-register";

logger.info("🚀 App starting...");

// CRITICAL: Only register PWA service worker on production hosts, NEVER on preview hosts.
// Edge browser has a known bug where the SW intercepts navigation requests and crashes
// when it encounters auth-bridge CORS redirects, making the entire tab unresponsive.
const isPreviewHost =
  typeof window !== "undefined" &&
  ((window.location.hostname.includes("preview--") &&
    window.location.hostname.endsWith(".lovable.app")) ||
    window.location.hostname.endsWith(".lovableproject.com"));
// Note: includes("preview--") matches both "preview--*" and "id-preview--*" hosts.

if (isPreviewHost) {
  // On preview hosts: aggressively clean up any existing SW/caches, but do NOT register new ones
  logger.info("🔧 Preview host detected - skipping PWA registration entirely");
  
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then(async (regs) => {
        if (regs.length > 0) {
          await Promise.all(regs.map((r) => r.unregister()));
          logger.info(`🧹 Unregistered ${regs.length} service worker(s)`);
        }
      })
      .catch((e) => logger.warn("SW cleanup failed:", e));
  }
} else {
  // On production hosts: enable full PWA functionality
  try {
    let updateSW: ((reloadPage?: boolean) => Promise<void>) | undefined;

    updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        updateSW?.(true);
      },
      onRegisteredSW(_swUrl, registration) {
        if (registration) {
          registration.update();
        }
      },
    });
  } catch (e) {
    logger.warn("PWA registration skipped:", e);
  }
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
